'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'

export default function ArcadeWidget({ challenge }: { challenge: any }) {
  const [selected, setSelected] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const router = useRouter()

  if (!challenge) return null

  const handlePlay = async (e: React.FormEvent) => {
    e.preventDefault()
    if (selected === null) return
    setLoading(true)

    try {
      const res = await fetch('/api/arcade/trivia', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: challenge.id, selectedIndex: selected })
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to submit')

      setResult(data)
      router.refresh()
    } catch (err: any) {
      alert(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-950 border border-indigo-500/30 rounded-[2.5ref] p-8 shadow-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 w-80 h-80 bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none"></div>
      
      <div className="relative z-10 space-y-6">
        <div className="flex items-center justify-between">
          <span className="bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30 px-3.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
            🕹️ DevYield Arcade
          </span>
          <span className="text-amber-400 font-mono font-bold text-sm bg-amber-500/10 border border-amber-500/20 px-3 py-1 rounded-xl">
            🏆 Jackpot: {challenge.pointsPool} PTS
          </span>
        </div>

        <div>
          <h3 className="text-xl font-black text-white mb-2">Daily Code Trivia Challenge</h3>
          <p className="text-sm text-slate-300 font-medium">{challenge.question}</p>
        </div>

        {result ? (
          <div className={`p-6 rounded-2xl border text-center space-y-2 ${
            result.isCorrect ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
          }`}>
            <h4 className="text-base font-black">{result.isCorrect ? '🎉 Correct! Well played.' : '❌ Incorrect! Better luck next time.'}</h4>
            <p className="text-xs opacity-80">Entry fee: 10 PTS deducted. Check your profile for updates!</p>
          </div>
        ) : (
          <form onSubmit={handlePlay} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {challenge.options.map((option: string, idx: number) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setSelected(idx)}
                  className={`p-4 rounded-xl border text-xs font-bold text-left transition-all ${
                    selected === idx 
                      ? 'bg-indigo-600 text-white border-indigo-400 shadow-lg shadow-indigo-600/30' 
                      : 'bg-slate-950 text-slate-300 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={selected === null || loading}
                className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-bold px-6 py-3 rounded-xl text-xs transition-colors shadow-lg shadow-fuchsia-600/30 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Answer (Cost: 10 PTS)'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}